import { Box, Stack } from '@mui/material';
import axios from 'axios';
import { Button, Loading } from 'components/elements';
import Icon from 'components/icon';
import Toast from 'components/toast';
import { EFeatures } from 'lib/feature/features.types';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { MixpanelWrapperInstance } from 'services/mixpanel/mixpanel-wrapper';
import { EventNames } from 'services/mixpanel/provider-app-mixpanel-events';
import { ClickableElement } from 'services/mixpanel/shared-mixpanel-service';
import { ChartContext } from 'state/contexts/chart';
import { FeaturesContext } from 'state/contexts/features';
import { PatientContext } from 'state/contexts/patient';
import { WeightsContext } from 'state/contexts/weights';
import { ActionTypes as ChartActionTypes } from 'state/reducers/chart';
import { ActionTypes as WeightsActionTypes } from 'state/reducers/weights';
import { debug } from 'utils/helpers';
import Chart from './chart/Chart';
import AddWeight from './chart/manage-weight/add-weight';
import { getChartDataFromCore, getWeightsFromCore, getWeightsFromHealthRecords } from './helpers';
import { ClickableContent, Stats } from './style';
import './weight-chart.css';
import WeightBulkDelete from './WeightBulkDelete';

const WeightChart = ({ patientId, visible }) => {
  const [loading, setLoading] = useState(true);
  const [showAddWeightModal, setShowAddWeightModal] = useState(false);
  const [selectedWeights, setSelectedWeights] = useState([]);

  const weightsCache = useRef([]);

  const { chartState, chartDispatch } = useContext(ChartContext);
  const { weightsState, weightsDispatch } = useContext(WeightsContext);
  const { patientState } = useContext(PatientContext);
  const { featuresState } = useContext(FeaturesContext);

  const readyToRenderWeights = useMemo(
    () => !!weightsState && chartState.patientId === patientId,
    [weightsState, patientId]
  );

  const currentMember = patientState.current;

  const getWeights = async (cancelToken) => {
    try {
      const { weights } = await (!!featuresState?.[EFeatures.ProviderGetWeightsFromHealthRecords]
        ? getWeightsFromHealthRecords(patientId, {
            cancelToken: cancelToken.token,
          })
        : getWeightsFromCore(patientId, {
            cancelToken: cancelToken.token,
          }));

      weightsDispatch({
        type: WeightsActionTypes.SET,
        payload: {
          height: {
            feet: currentMember.feet,
            inches: currentMember.inches,
          },
          initialWeightInLbs: !!currentMember.initial_weight
            ? Number.parseFloat(`${currentMember.initial_weight}`)
            : null,
          weightsInGrams: weights,
        },
      });
      setLoading(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        debug(err);
        setLoading(false);
      }
    }
  };

  const firstWeightsLoad = async (cancelToken) => {
    if (chartState.patientId !== patientId || !chartState.data.weights.values) {
      if (chartState.patientId !== patientId) {
        chartDispatch({ type: ChartActionTypes.CLEAR });
      }
      await getWeights(cancelToken);
    } else {
      setLoading(false);
    }
  };

  const toggleAddWeightModal = () => {
    if (!showAddWeightModal) {
      MixpanelWrapperInstance.track(EventNames.MemberProfileAddWeightOpenModal, {
        targetType: ClickableElement.BUTTON,
        targetLocation: 'weights',
        memberId: currentMember?.id,
      });
    }

    setShowAddWeightModal((prevState) => !prevState);
  };

  const handleDeleteWeights = async () => {
    Toast.show('success', 'Weights were removed correctly');

    const cancelToken = axios.CancelToken.source();

    setSelectedWeights([]);
    weightsCache.current = [];
    await getWeights(cancelToken);
  };

  const handleMarkerClick = (weight) => {
    const found = weightsCache.current.some((m) => m.id === weight.id);

    if (!found) {
      const updatedWeightsList = [...weightsCache.current, weight];

      weightsCache.current = updatedWeightsList;
      setSelectedWeights(updatedWeightsList);
    }
  };

  const handleMarkersSelected = (markers) => {
    if (markers.length === 0) {
      setSelectedWeights([]);
      weightsCache.current = [];

      return;
    }

    const deletableWeights = markers.filter((m) =>
      weightsCache.current.every((w) => w.id !== m.id)
    );

    const updatedWeightsList = [...weightsCache.current, ...deletableWeights];

    weightsCache.current = updatedWeightsList;
    setSelectedWeights(updatedWeightsList);
  };

  useEffect(() => {
    setLoading(true);

    const cancelToken = axios.CancelToken.source();
    (async () => {
      firstWeightsLoad(cancelToken);
    })();

    return () => {
      cancelToken.cancel();
    };
  }, [patientId]);

  useEffect(() => {
    if (!!weightsState) {
      const cancelToken = axios.CancelToken.source();

      (async () => {
        const { bmi, weight } = weightsState;

        // The type for coreChartData is not accurate
        const coreChartData = await getChartDataFromCore(patientId, {
          cancelToken: cancelToken.token,
        });

        // Still dispatching this since this might load other data that is used at other places within the app
        chartDispatch({
          type: ChartActionTypes.SET,
          payload: {
            patientId,
            data: {
              ...coreChartData,
              current_bmi: bmi.current,
              initial_bmi: bmi.initial,
              initial_weight: weight.initialWeightInLbs,
              weight_loss_percentage: weight.stats.weightLossPercentage,
              weight_loss_total: weight.stats.weightLoss,
            },
            type: 'weights',
          },
        });
      })();

      return () => {
        cancelToken.cancel();
      };
    }
  }, [weightsState]);

  if (!visible) {
    return null;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {showAddWeightModal && <AddWeight onClose={toggleAddWeightModal} />}
      {readyToRenderWeights && (
        <Box className='weight-chart-container'>
          <Stack className='weight-stats-container'>
            <Stats>
              {weightsState.weight.initial && (
                <div>
                  <h4>Starting Weight (BMI)</h4>
                  <p>
                    {weightsState.weight.initial} lbs {`(${weightsState.bmi.initial || 'NA'})`}
                  </p>
                </div>
              )}
              {weightsState.weight.initial && weightsState.weight.stats.weightLoss && (
                <div>
                  <h4>Current Weight (BMI)</h4>
                  <p>
                    {(weightsState.weight.initial - weightsState.weight.stats.weightLoss).toFixed(
                      2
                    )}{' '}
                    lbs {`(${weightsState.bmi.current || 'NA'})`}
                  </p>
                </div>
              )}
              {weightsState.weight.stats.weightLoss && (
                <div>
                  <h4>Weight Lost</h4>
                  <span>(From Starting Weight)</span>
                  <p>
                    {weightsState.weight.stats.weightLoss} lbs (
                    {`${weightsState.weight.stats.weightLossPercentage}` || 'NA'}%)
                  </p>
                </div>
              )}
            </Stats>
            <Box className='chart-top-action-container'>
              <ClickableContent
                data-tip='Add New Weight'
                data-for='add-new-weight'
                key='add-new-weight'>
                <ReactTooltip id='add-new-weight' place='top' type='dark' effect='float' />
                <Button color='white' onClick={toggleAddWeightModal}>
                  <Icon icon='plus' background='teal' color='white' size={10} />
                </Button>
              </ClickableContent>
            </Box>

            {!!weightsState.weight.weights?.length && (
              <Chart onMarkerClick={handleMarkerClick} onMarkersSelected={handleMarkersSelected} />
            )}
          </Stack>

          {selectedWeights.length > 0 && (
            <WeightBulkDelete weights={selectedWeights} onUpdateWeights={handleDeleteWeights} />
          )}
        </Box>
      )}
    </>
  );
};

WeightChart.propTypes = {
  patientId: PropTypes.number.isRequired,
};

WeightChart.defaultProps = {};

export default WeightChart;
